import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getVideoLessonQuestions, transformBackendQuestions, submitQuestionAnswer } from "../../../lib/questionsApi";
import { saveTrialProgress, getTrialProgress } from "../../../lib/trialLessonProgress";
import css from "./QuizDesign/quizDesign.module.css";
import QuizSidebar from "./QuizDesign/QuizSidebar";
import QuizCompletion from "./QuizDesign/QuizCompletion";
import { CheckIcon, XIcon, ArrowIcon, BookIcon, QuizIcon } from "./QuizDesign/QuizIcons";

const LETTERS = ["A", "B", "C", "D", "E"];

const shuffleArr = (a) => {
    const arr = [...a];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
};

const QuestionCardNew = ({
    videoLessonId,
    onTestComplete,
    onActivityComplete,
    maxAttempts = 2,
    enabledTestTypes = { yesNo: true, textVoice: true, dragDrop: true, translationMatch: true },
    initialQuestions = null,
    savedState = null,
    onStateChange = null,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const isTrialLesson = location.pathname === "/trial-lesson";

    // Замінити всі useState на початку компонента:
    const [questions, setQuestions] = useState(savedState?.questions || []);
    const [loading, setLoading] = useState(!savedState?.questions?.length && !initialQuestions?.length);
    const [currentIndex, setCurrentIndex] = useState(savedState?.currentIndex ?? 0);
    const [score, setScore] = useState(savedState?.score ?? 0);
    const [results, setResults] = useState(savedState?.results || []);
    const [answered, setAnswered] = useState(savedState?.answered ?? false);
    const [selectedIdx, setSelectedIdx] = useState(savedState?.selectedIdx ?? null);
    const [isRight, setIsRight] = useState(savedState?.isRight ?? null);
    const [feedback, setFeedback] = useState(savedState?.feedback ?? null);
    const [submitting, setSubmitting] = useState(false);
    const [showCompletion, setShowCompletion] = useState(savedState?.showCompletion ?? false);
    const [animClass, setAnimClass] = useState("");
    const [textAnswer, setTextAnswer] = useState(savedState?.textAnswer ?? "");
    const [showCorrectBlank, setShowCorrectBlank] = useState(savedState?.showCorrectBlank ?? false);
    const [bpSelectedWords, setBpSelectedWords] = useState(savedState?.bpSelectedWords || []);
    const [bpShuffledBank, setBpShuffledBank] = useState([]);
    const [reviewIndex, setReviewIndex] = useState(null);
    const [allSelectedIdx, setAllSelectedIdx] = useState(savedState?.allSelectedIdx || {});
    const [allFeedback, setAllFeedback] = useState(savedState?.allFeedback || {});
    const [allBpWords, setAllBpWords] = useState(savedState?.allBpWords || {});
    const [allTextAnswers, setAllTextAnswers] = useState(savedState?.allTextAnswers || {});
    const [allShowCorrect, setAllShowCorrect] = useState(savedState?.allShowCorrect || {});

    const cardRef = useRef(null);

    const saveState = useCallback(
        (overrides = {}) => {
            if (!onStateChange) return;

            const stateToSave = {
                questions,
                currentIndex,
                score,
                results,
                answered,
                selectedIdx,
                isRight,
                feedback,
                showCompletion,
                textAnswer,
                showCorrectBlank,
                bpSelectedWords,
                allSelectedIdx,
                allFeedback,
                allBpWords,
                allTextAnswers,
                allShowCorrect,
                ...overrides,
            };

            onStateChange(stateToSave);

            // Also save to localStorage for trial lesson
            if (isTrialLesson) {
                saveTrialProgress("quiz", stateToSave);
            }
        },
        [
            questions,
            currentIndex,
            score,
            results,
            answered,
            selectedIdx,
            isRight,
            feedback,
            showCompletion,
            textAnswer,
            showCorrectBlank,
            bpSelectedWords,
            allSelectedIdx,
            allFeedback,
            allBpWords,
            allTextAnswers,
            allShowCorrect,
            onStateChange,
            isTrialLesson,
        ],
    );

    useEffect(() => {
        const fetchQuestions = async () => {
            // Якщо є збережені питання — використати їх, не рефетчити
            if (savedState?.questions?.length > 0) {
                setQuestions(savedState.questions);
                setLoading(false);
                return;
            }

            // Якщо є initialQuestions з пропсів — трансформувати і використати
            if (initialQuestions && initialQuestions.length > 0) {
                const transformed = transformBackendQuestions(initialQuestions);
                setQuestions(transformed.slice(0, 10));
                setLoading(false);
                return;
            }

            if (!videoLessonId) {
                setLoading(false);
                return;
            }

            try {
                const data = await getVideoLessonQuestions(videoLessonId);
                const raw = data.questions || data.results || data;
                const transformed = transformBackendQuestions(Array.isArray(raw) ? raw : []);
                setQuestions(transformed.slice(0, 10));
            } catch (err) {
                console.error("[QuestionCardNew] Failed to load questions", err);
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [videoLessonId]);

    const totalQuestions = questions.length;
    const isReviewMode = reviewIndex !== null;
    const displayIndex = isReviewMode ? reviewIndex : currentIndex;
    const current = questions[displayIndex];

    // В review mode беремо збережені дані, інакше — поточний стейт
    const effectiveAnswered = isReviewMode ? true : answered;
    const effectiveSelectedIdx = isReviewMode ? allSelectedIdx[reviewIndex] : selectedIdx;
    const effectiveIsRight = isReviewMode ? results[reviewIndex] : isRight;
    const effectiveFeedback = isReviewMode ? allFeedback[reviewIndex] : feedback;
    const effectiveBpWords = isReviewMode ? allBpWords[reviewIndex] || [] : bpSelectedWords;
    const effectiveTextAnswer = isReviewMode ? allTextAnswers[reviewIndex] || "" : textAnswer;
    const effectiveShowCorrect = isReviewMode ? allShowCorrect[reviewIndex] || false : showCorrectBlank;

    const getQuizTypeLabel = (q) => {
        if (!q) return "";
        switch (q.type) {
            case "translationMatch":
                return "Word meaning";
            case "yesNo":
                return "True / False";
            case "textVoice":
                return "Open Answer";
            case "dragDrop":
                return "Fill in the blank";
            case "fillInBlank":
                return "Fill in the blank";
            case "buildPhrase":
                return "Build the phrase";
            default:
                return "Quiz";
        }
    };

    const getCorrectOptionIndex = (question, feedbackText) => {
        if (!question?.answerOptions || !feedbackText) return -1;
        const match = feedbackText.match(/correct (?:answer|phrase|translation)\s+is\s+['"]([^'"]+)['"]/i);
        const word = match?.[1]?.trim().toLowerCase();
        if (!word) return -1;
        return question.answerOptions.findIndex((opt) => (opt || "").trim().toLowerCase() === word);
    };

    const getCorrectAnswerText = (feedbackText) => {
        if (!feedbackText) return "";
        const match = feedbackText.match(/correct (?:answer|phrase|translation)\s+is\s+['"]([^'"]+)['"]/i);
        return match?.[1] || feedbackText;
    };
    
    const handlePickOption = useCallback(
        async (idx) => {
            if (isReviewMode || answered || submitting) return;
            setSelectedIdx(idx);
            setSubmitting(true);

            const q = questions[currentIndex];
            const selectedText = q.answerOptions[idx];

            try {
                const res = await submitQuestionAnswer(q.id, selectedText, q.type);
                const correct = res.result === "correct" || res.answer_status === "RIGHT";
                const fb = res.feedback || res.ai_feedback || "";

                setIsRight(correct);
                if (correct) setScore((s) => s + 1);

                const newResults = [...results];
                newResults[currentIndex] = correct;
                setResults(newResults);
                setFeedback(fb);
                setAnswered(true);

                const stateToSave = {
                    questions,
                    currentIndex,
                    score: correct ? score + 1 : score,
                    results: newResults,
                    answered: true,
                    selectedIdx: idx,
                    isRight: correct,
                    feedback: fb,
                    showCompletion: false,
                    allSelectedIdx: { ...allSelectedIdx, [currentIndex]: idx },
                    allFeedback: { ...allFeedback, [currentIndex]: fb },
                };
                if (onStateChange) onStateChange(stateToSave);

                // Зберігаємо для review
                setAllSelectedIdx((prev) => ({ ...prev, [currentIndex]: idx }));
                setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));

                if (!correct && q.type === "fillInBlank") {
                    setTimeout(() => {
                        setShowCorrectBlank(true);
                        setAllShowCorrect((prev) => ({ ...prev, [currentIndex]: true }));
                    }, 800);
                }
            } catch (err) {
                const fb = err.message || "Error submitting answer";
                setIsRight(false);
                const newResults = [...results];
                newResults[currentIndex] = false;
                setResults(newResults);
                setFeedback(fb);
                setAnswered(true);
                setAllSelectedIdx((prev) => ({ ...prev, [currentIndex]: idx }));
                setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
                if (q.type === "fillInBlank") {
                    setTimeout(() => {
                        setShowCorrectBlank(true);
                        setAllShowCorrect((prev) => ({ ...prev, [currentIndex]: true }));
                    }, 800);
                }
            } finally {
                setSubmitting(false);
            }
        },
        [isReviewMode, answered, submitting, questions, currentIndex, results],
    );

    useEffect(() => {
        if (current && current.type === "buildPhrase" && !isReviewMode) {
            setBpShuffledBank(shuffleArr(current.words || []));
            setBpSelectedWords([]);
        }
    }, [currentIndex, current, isReviewMode]);

    const handleBpPickWord = useCallback(
        (w) => {
            if (isReviewMode || answered || submitting) return;
            if (bpSelectedWords.includes(w)) return;
            setBpSelectedWords((prev) => [...prev, w]);
        },
        [isReviewMode, answered, submitting, bpSelectedWords],
    );

    const handleBpRemoveWord = useCallback(
        (w) => {
            if (isReviewMode || answered || submitting) return;
            setBpSelectedWords((prev) => prev.filter((x) => x !== w));
        },
        [isReviewMode, answered, submitting],
    );

    const handleBpClear = useCallback(() => {
        if (isReviewMode || answered || submitting) return;
        setBpSelectedWords([]);
    }, [isReviewMode, answered, submitting]);

    const handleBpSubmit = useCallback(async () => {
        if (isReviewMode || answered || submitting || bpSelectedWords.length === 0) return;
        setSubmitting(true);

        const q = questions[currentIndex];
        try {
            const res = await submitQuestionAnswer(q.id, bpSelectedWords, "buildPhrase");
            const correct = res.result === "correct" || res.answer_status === "RIGHT";
            const fb = res.feedback || res.ai_feedback || "";

            setIsRight(correct);
            if (correct) setScore((s) => s + 1);
            const newResults = [...results];
            newResults[currentIndex] = correct;
            setResults(newResults);
            setFeedback(fb);
            setAnswered(true);

            if (onStateChange)
                onStateChange({
                    questions,
                    currentIndex,
                    score: correct ? score + 1 : score,
                    results: newResults,
                    answered: true,
                    isRight: correct,
                    feedback: fb,
                    allBpWords: { ...allBpWords, [currentIndex]: bpSelectedWords },
                    allFeedback: { ...allFeedback, [currentIndex]: fb },
                    allSelectedIdx,
                    allTextAnswers,
                    allShowCorrect,
                    showCompletion: false,
                    selectedIdx,
                    textAnswer,
                    showCorrectBlank,
                    bpSelectedWords,
                });

            setAllBpWords((prev) => ({ ...prev, [currentIndex]: bpSelectedWords }));
            setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
        } catch (err) {
            const fb = err.message || "Error";
            setIsRight(false);
            const newResults = [...results];
            newResults[currentIndex] = false;
            setResults(newResults);
            setFeedback(fb);
            setAnswered(true);
            setAllBpWords((prev) => ({ ...prev, [currentIndex]: bpSelectedWords }));
            setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
        } finally {
            setSubmitting(false);
        }
    }, [isReviewMode, answered, submitting, bpSelectedWords, questions, currentIndex, results]);

    const handleAnswerYesNo = useCallback(
        async (val) => {
            if (isReviewMode || answered || submitting) return;
            setSelectedIdx(val);
            setSubmitting(true);

            const q = questions[currentIndex];
            try {
                const res = await submitQuestionAnswer(q.id, val, "yesNo");
                const correct = res.result === "correct" || res.answer_status === "RIGHT";
                const fb = res.feedback || res.ai_feedback || "";

                setIsRight(correct);
                if (correct) setScore((s) => s + 1);
                const newResults = [...results];
                newResults[currentIndex] = correct;
                setResults(newResults);
                setFeedback(fb);
                setAnswered(true);

                if (onStateChange)
                    onStateChange({
                        questions,
                        currentIndex,
                        score: correct ? score + 1 : score,
                        results: newResults,
                        answered: true,
                        selectedIdx: val,
                        isRight: correct,
                        feedback: fb,
                        allSelectedIdx: { ...allSelectedIdx, [currentIndex]: val },
                        allFeedback: { ...allFeedback, [currentIndex]: fb },
                        allBpWords,
                        allTextAnswers,
                        allShowCorrect,
                        showCompletion: false,
                        textAnswer,
                        showCorrectBlank,
                        bpSelectedWords,
                    });

                setAllSelectedIdx((prev) => ({ ...prev, [currentIndex]: val }));
                setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
            } catch (err) {
                const fb = err.message || "Error";
                setIsRight(false);
                const newResults = [...results];
                newResults[currentIndex] = false;
                setResults(newResults);
                setFeedback(fb);
                setAnswered(true);
                setAllSelectedIdx((prev) => ({ ...prev, [currentIndex]: val }));
                setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
            } finally {
                setSubmitting(false);
            }
        },
        [isReviewMode, answered, submitting, questions, currentIndex, results],
    );

    const handleSubmitText = useCallback(async () => {
        if (isReviewMode || answered || submitting || !textAnswer.trim()) return;
        setSubmitting(true);

        const q = questions[currentIndex];
        try {
            const res = await submitQuestionAnswer(q.id, textAnswer, q.type);
            const correct = res.result === "correct" || res.answer_status === "RIGHT";
            const fb = res.feedback || res.ai_feedback || "";

            setIsRight(correct);
            if (correct) setScore((s) => s + 1);
            const newResults = [...results];
            newResults[currentIndex] = correct;
            setResults(newResults);
            setFeedback(fb);
            setAnswered(true);
            if (onStateChange)
                onStateChange({
                    questions,
                    currentIndex,
                    score: correct ? score + 1 : score,
                    results: newResults,
                    answered: true,
                    isRight: correct,
                    feedback: fb,
                    textAnswer,
                    allTextAnswers: { ...allTextAnswers, [currentIndex]: textAnswer },
                    allFeedback: { ...allFeedback, [currentIndex]: fb },
                    allSelectedIdx,
                    allBpWords,
                    allShowCorrect,
                    showCompletion: false,
                    selectedIdx,
                    showCorrectBlank,
                    bpSelectedWords,
                });

            setAllTextAnswers((prev) => ({ ...prev, [currentIndex]: textAnswer }));
            setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
        } catch (err) {
            const fb = err.message || "Error";
            setIsRight(false);
            const newResults = [...results];
            newResults[currentIndex] = false;
            setResults(newResults);
            setFeedback(fb);
            setAnswered(true);
            setAllTextAnswers((prev) => ({ ...prev, [currentIndex]: textAnswer }));
            setAllFeedback((prev) => ({ ...prev, [currentIndex]: fb }));
        } finally {
            setSubmitting(false);
        }
    }, [isReviewMode, answered, submitting, textAnswer, questions, currentIndex, results]);

    const handleNext = useCallback(() => {
        if (isReviewMode) {
            setReviewIndex(null);
            return;
        }
        if (!answered) return;

        if (currentIndex >= totalQuestions - 1) {
            setShowCompletion(true);
            if (onTestComplete)
                onTestComplete({
                    score,
                    total: totalQuestions,
                    percentage: Math.round((score / totalQuestions) * 100),
                });
            return;
        }

        setAnimClass(css.qCardAnimOut);
        setTimeout(() => {
            setCurrentIndex((i) => i + 1);
            setAnswered(false);
            setSelectedIdx(null);
            setIsRight(null);
            setFeedback(null);
            setTextAnswer("");
            setShowCorrectBlank(false);
            setBpSelectedWords([]);
            setAnimClass(css.qCardAnimIn);
            requestAnimationFrame(() => requestAnimationFrame(() => setAnimClass("")));
        }, 200);
    }, [isReviewMode, answered, currentIndex, totalQuestions, onTestComplete, score]);

    const handleGoToQuestion = useCallback(
        (idx) => {
            if (results[idx] === undefined) return;
            setReviewIndex(idx);
        },
        [results],
    );

    const handleExitReview = useCallback(() => {
        setReviewIndex(null);
        setShowCompletion(true);
    }, []);

    const handleReview = () => {
        setShowCompletion(false);
        setReviewIndex(0);
    };

    const handleContinue = () => {
        if (onActivityComplete) onActivityComplete(Math.round((score / totalQuestions) * 10));
        if (window.advanceStep) window.advanceStep();
    };

    if (loading)
        return (
            <div className={css.quizWrap}>
                <div style={{ padding: 40, textAlign: "center", color: "var(--t3)" }}>{t("Loading...")}</div>
            </div>
        );
    if (questions.length === 0)
        return (
            <div className={css.quizWrap}>
                <div style={{ padding: 40, textAlign: "center", color: "var(--t3)" }}>
                    {t("No questions available.")}
                </div>
            </div>
        );
    if (showCompletion)
        return (
            <div className={css.quizWrap}>
                <QuizCompletion
                    score={score}
                    totalQuestions={totalQuestions}
                    onContinue={handleContinue}
                    onReview={handleReview}
                />
            </div>
        );

    return (
        <div className={css.quizWrap}>
            {/* Progress bar */}
            <div className={css.flowBar}>
                <div className={css.flowProgress}>
                    <span className={css.flowQ}>
                        {currentIndex + 1} / {totalQuestions}
                    </span>
                    <div className={css.progWrap}>
                        <div
                            className={css.progFill}
                            style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Review banner */}
            {isReviewMode && (
                <div
                    style={{
                        background: "#F0F9FF",
                        border: "1.5px solid #BAE6FD",
                        borderRadius: 12,
                        padding: "10px 16px",
                        marginBottom: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                    }}>
                    <span style={{ fontSize: 13, color: "#0369A1", fontWeight: 600 }}>
                        👁 {t("Review mode")} — {t("Question")} {reviewIndex + 1}
                    </span>
                    <button
                        onClick={handleExitReview}
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "5px 14px",
                            borderRadius: 8,
                            border: "1.5px solid #BAE6FD",
                            background: "#fff",
                            color: "#0369A1",
                            cursor: "pointer",
                        }}>
                        ← {t("Back to current")}
                    </button>
                </div>
            )}

            <div className={css.quizLayout}>
                <div className={css.quizMain} style={{ flex: 1, minWidth: 0 }}>
                    {/* TRANSLATION MATCH & FILL IN BLANK */}
                    {(current.type === "translationMatch" || current.type === "fillInBlank") && (
                        <>
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                {current.type === "translationMatch" ? (
                                    <>
                                        <div className={css.qBadge}>
                                            <BookIcon style={{ width: 13, height: 13 }} />
                                            {t("Word meaning")}
                                        </div>
                                        <div className={css.qInstr}>{t("Choose the correct meaning of the word.")}</div>
                                        <div className={css.qWordWrap}>
                                            <div className={css.qWord}>{current.text}</div>
                                            <div className={css.qMeans}>{t("means")}:</div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className={css.qBadge} style={{ background: "#E0F2FE", color: "#0284C7" }}>
                                            <BookIcon style={{ width: 13, height: 13 }} />
                                            {t("Fill in the blank")}
                                        </div>
                                        <div className={css.qInstr}>
                                            {t("Choose the word that correctly completes the sentence.")}
                                        </div>
                                        <div className={css.qSen}>
                                            {(() => {
                                                const parts = current.text.split("___");
                                                if (parts.length < 2) return current.text;
                                                let blankText = "       ";
                                                let blankClass = css.blankEmpty;
                                                if (effectiveAnswered) {
                                                    // ⚠️ ФІКС: раніше тут була гілка effectiveShowCorrect, яка після
                                                    // затримки намагалась "розкрити" правильне слово, підставляючи
                                                    // effectiveFeedback — а це повний текст пояснення з бека
                                                    // ("Incorrect. The correct answer is 'cooking'. ..."), а не слово.
                                                    // У QuestionCardNew немає локального знання правильної відповіді
                                                    // (на відміну від FillBlankQuiz.jsx, що звіряв current.ci) — весь
                                                    // результат приходить з submitQuestionAnswer(). Тож просто лишаємо
                                                    // в бланку те, що обрав користувач, з відповідним кольором; повне
                                                    // пояснення показується окремо у блоці FEEDBACK нижче.
                                                    blankText = current.answerOptions[effectiveSelectedIdx];
                                                    blankClass = effectiveIsRight
                                                        ? `${css.blankFilled} ${css.blankOk}`
                                                        : `${css.blankFilled} ${css.blankNo}`;
                                                }
                                                return (
                                                    <>
                                                        {parts[0]}
                                                        <span className={blankClass}>{blankText}</span>
                                                        {parts[1]}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className={css.qOptsVertical}>
                                {(() => {
                                    const correctOptionIdx = !effectiveIsRight
                                        ? getCorrectOptionIndex(current, effectiveFeedback)
                                        : -1;

                                    return current.answerOptions.map((opt, i) => {
                                        let optClass = css.qOpt;
                                        let letterClass = css.qOptLetter;
                                        let textClass = css.qOptText;
                                        let icon = null;

                                        if (effectiveAnswered) {
                                            if (i === effectiveSelectedIdx) {
                                                if (effectiveIsRight) {
                                                    optClass += ` ${css.qOptCorrect}`;
                                                    letterClass = css.qOptLetterCorrect;
                                                    textClass = css.qOptTextCorrect;
                                                    icon = (
                                                        <span className={`${css.qOptIcoShow} ${css.icoOk}`}>
                                                            <CheckIcon strokeWidth={3} />
                                                        </span>
                                                    );
                                                } else {
                                                    optClass += ` ${css.qOptWrong}`;
                                                    letterClass = css.qOptLetterWrong;
                                                    textClass = css.qOptTextWrong;
                                                    icon = (
                                                        <span className={`${css.qOptIcoShow} ${css.icoNo}`}>
                                                            <XIcon />
                                                        </span>
                                                    );
                                                }
                                            } else if (i === correctOptionIdx) {
                                                optClass += ` ${css.qOptCorrect} ${css.qOptDisabled}`;
                                                letterClass = css.qOptLetterCorrect;
                                                textClass = css.qOptTextCorrect;
                                                icon = (
                                                    <span className={`${css.qOptIcoShow} ${css.icoOk}`}>
                                                        <CheckIcon strokeWidth={3} />
                                                    </span>
                                                );
                                            } else {
                                                optClass += ` ${css.qOptDisabled}`;
                                            }
                                        }

                                        return (
                                            <div
                                                key={i}
                                                className={optClass}
                                                onClick={() => !isReviewMode && handlePickOption(i)}
                                                style={effectiveAnswered || submitting ? { cursor: "default" } : {}}>
                                                <span className={letterClass}>{LETTERS[i]}</span>
                                                <span className={textClass}>{opt}</span>
                                                {icon}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </>
                    )}

                    {/* BUILD PHRASE */}
                    {current.type === "buildPhrase" && (
                        <>
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                <div className={css.qBadge} style={{ background: "#FEF3C7", color: "#B45309" }}>
                                    <QuizIcon style={{ width: 13, height: 13 }} />
                                    {t("Build the phrase")}
                                </div>
                                <div className={css.qInstr}>
                                    {t("Tap the words in the correct order to build the English phrase.")}
                                </div>
                                <div className={css.prompt}>
                                    <div className={css.promptIcon}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M2 12h20" />
                                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                        </svg>
                                    </div>
                                    <div className={css.promptText}>{current.prompt}</div>
                                </div>

                                <div className={css.ansLabel}>{t("Your phrase")}</div>
                                <div
                                    className={`${css.ansArea}${effectiveBpWords.length > 0 ? ` ${css.has}` : ""}${effectiveAnswered ? (effectiveIsRight ? ` ${css.ok}` : ` ${css.no}`) : ""}`}>
                                    {effectiveBpWords.length === 0 ? (
                                        <span className={css.ansEmpty}>
                                            {t("Tap the words below to build the phrase.")}
                                        </span>
                                    ) : (
                                        effectiveBpWords.map((w, idx) => (
                                            <div
                                                key={idx}
                                                className={`${css.ansChip}${effectiveAnswered ? (effectiveIsRight ? ` ${css.ok}` : ` ${css.no}`) : ""}`}
                                                onClick={() => !isReviewMode && handleBpRemoveWord(w)}>
                                                {w}
                                                {!effectiveAnswered && !isReviewMode && (
                                                    <svg
                                                        className={css.x}
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round">
                                                        <path d="M18 6 6 18" />
                                                        <path d="m6 6 12 12" />
                                                    </svg>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className={css.bankLabel}>{t("Words")}</div>
                                <div className={css.bank}>
                                    {(isReviewMode ? shuffleArr(current.words || []) : bpShuffledBank).map((w, idx) => {
                                        const used = effectiveBpWords.includes(w);
                                        return (
                                            <div
                                                key={idx}
                                                className={`${css.wchip} ${used ? css.used : ""}`}
                                                onClick={() => !isReviewMode && handleBpPickWord(w)}>
                                                {w}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {!effectiveAnswered && !isReviewMode && (
                                <div className={css.qAct} style={{ display: "flex", gap: 10 }}>
                                    {bpSelectedWords.length > 0 && (
                                        <button className={css.qbClear} onClick={handleBpClear}>
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                                <path d="M3 3v5h5" />
                                            </svg>
                                            {t("Clear")}
                                        </button>
                                    )}
                                    <button
                                        className={bpSelectedWords.length > 0 ? css.qBtnAct : css.qBtnDis}
                                        onClick={handleBpSubmit}
                                        disabled={bpSelectedWords.length === 0 || submitting}>
                                        {submitting ? t("Checking...") : t("Check phrase")}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* YES/NO */}
                    {current.type === "yesNo" && (
                        <>
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                <div className={css.qBadge} style={{ background: "var(--i50)", color: "var(--i500)" }}>
                                    {t("True / False")}
                                </div>
                                <div className={css.qInstr}>{t("Determine if the statement is correct.")}</div>
                                <div className={css.qText}>{current.text}</div>
                            </div>

                            <div className={css.qOptsTF}>
                                {["yes", "no"].map((val) => {
                                    let optClass = css.qOptTF;
                                    if (effectiveAnswered) {
                                        if (effectiveSelectedIdx === val) {
                                            optClass += effectiveIsRight ? ` ${css.qOptCorrect}` : ` ${css.qOptWrong}`;
                                        } else {
                                            optClass += ` ${css.qOptDisabled}`;
                                        }
                                    }
                                    return (
                                        <div
                                            key={val}
                                            className={optClass}
                                            onClick={() => !isReviewMode && handleAnswerYesNo(val)}
                                            style={effectiveAnswered || submitting ? { cursor: "default" } : {}}>
                                            <span className={css.qOptLabel}>{val === "yes" ? "✓" : "✗"}</span>
                                            <span className={css.qOptText}>
                                                {val === "yes"
                                                    ? t("True / False").split(" / ")[0]
                                                    : t("True / False").split(" / ")[1]}
                                            </span>
                                            {effectiveAnswered &&
                                                effectiveSelectedIdx === val &&
                                                (effectiveIsRight ? (
                                                    <span className={`${css.qOptIcoShow} ${css.icoOk}`}>
                                                        <CheckIcon strokeWidth={3} />
                                                    </span>
                                                ) : (
                                                    <span className={`${css.qOptIcoShow} ${css.icoNo}`}>
                                                        <XIcon />
                                                    </span>
                                                ))}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* TEXT/VOICE */}
                    {(current.type === "textVoice" || current.type === "dragDrop") && (
                        <>
                            <div className={`${css.qCard} ${animClass}`} ref={cardRef}>
                                <div className={css.qBadge} style={{ background: "#FFF1F2", color: "#E11D48" }}>
                                    {t("Open Answer")}
                                </div>
                                <div className={css.qInstr}>{t("Answer the question.")}</div>
                                <div className={css.qText}>{current.text}</div>
                            </div>
                            <div style={{ marginTop: 0 }}>
                                <textarea
                                    value={effectiveTextAnswer}
                                    onChange={(e) => !isReviewMode && setTextAnswer(e.target.value)}
                                    disabled={effectiveAnswered || submitting || isReviewMode}
                                    placeholder={t("Type your answer here...")}
                                    style={{
                                        width: "100%",
                                        minHeight: "100px",
                                        padding: "16px",
                                        borderRadius: "16px",
                                        border: `2px solid ${effectiveAnswered ? (effectiveIsRight ? "#10B981" : "#EF4444") : "#E5E7EB"}`,
                                        fontSize: "16px",
                                        fontFamily: "'Sora', sans-serif",
                                        outline: "none",
                                        background: effectiveAnswered
                                            ? effectiveIsRight
                                                ? "#F0FDF4"
                                                : "#FFF1F2"
                                            : "#fff",
                                        resize: "vertical",
                                        transition: "border-color .2s",
                                    }}
                                />
                            </div>
                            {!effectiveAnswered && !isReviewMode && (
                                <div className={css.qAct}>
                                    <button
                                        className={textAnswer.trim() ? css.qBtnAct : css.qBtnDis}
                                        onClick={handleSubmitText}
                                        disabled={!textAnswer.trim() || submitting}>
                                        {submitting ? t("Checking...") : t("Submit answer")}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* FEEDBACK */}
                    {effectiveAnswered && (
                        <div className={`${css.qFb} ${css.qFbShow} ${effectiveIsRight ? css.qFbOk : css.qFbNo}`}>
                            <div className={effectiveIsRight ? css.qFbTitleOk : css.qFbTitleNo}>
                                {effectiveIsRight ? t("Correct!") : t("Not quite")}
                            </div>
                            <div className={css.qFbDesc}>
                                {effectiveFeedback && (
                                    <>
                                        {(current.type === "translationMatch" || current.type === "fillInBlank") &&
                                            (effectiveIsRight ? (
                                                <>
                                                    <strong>
                                                        {current.type === "fillInBlank"
                                                            ? current.answerOptions[effectiveSelectedIdx]
                                                            : current.text}
                                                    </strong>
                                                </>
                                            ) : (
                                                <>
                                                    {t("Correct answer")}: <strong>{getCorrectAnswerText(effectiveFeedback)}</strong>
                                                </>
                                            ))}
                                        {current.type === "buildPhrase" &&
                                            (effectiveIsRight ? (
                                                <>
                                                    "{effectiveBpWords.join(" ")}" — {current.prompt}
                                                </>
                                            ) : (
                                                <>
                                                    {t("Correct answer")}: <strong>{getCorrectAnswerText(effectiveFeedback)}</strong>
                                                </>
                                            ))}
                                        {current.type !== "translationMatch" &&
                                            current.type !== "fillInBlank" &&
                                            current.type !== "buildPhrase" && <>{effectiveFeedback}</>}
                                    </>
                                )}
                                {!effectiveFeedback && effectiveIsRight && <>{t("Great job!")}</>}
                                {!effectiveFeedback && !effectiveIsRight && current.type === "buildPhrase" && (
                                    <>{t("Try rearranging the words in a different order.")}</>
                                )}
                                {!effectiveFeedback && !effectiveIsRight && current.type !== "buildPhrase" && (
                                    <>{t("Try to review the video material again.")}</>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ACTION BUTTON */}
                    <div className={css.qAct}>
                        {isReviewMode ? (
                            <button className={css.qBtnAct} onClick={handleExitReview}>
                                ← {t("Back to current")}
                            </button>
                        ) : effectiveAnswered ? (
                            <button className={css.qBtnAct} onClick={handleNext}>
                                {currentIndex >= totalQuestions - 1 ? (
                                    <>
                                        {t("Finish quiz")}{" "}
                                        <CheckIcon strokeWidth={2} style={{ width: 16, height: 16 }} />
                                    </>
                                ) : (
                                    <>
                                        {t("Next question")} <ArrowIcon style={{ width: 16, height: 16 }} />
                                    </>
                                )}
                            </button>
                        ) : (
                            current.type !== "textVoice" &&
                            current.type !== "dragDrop" &&
                            current.type !== "buildPhrase" && (
                                <button className={css.qBtnDis}>
                                    {submitting ? t("Checking...") : t("Choose an answer")}
                                </button>
                            )
                        )}
                    </div>
                </div>

                <QuizSidebar
                    currentIndex={displayIndex}
                    totalQuestions={totalQuestions}
                    results={results}
                    score={score}
                    quizType={getQuizTypeLabel(current)}
                    onGoToQuestion={handleGoToQuestion}
                />
            </div>
        </div>
    );
};

export default QuestionCardNew;
